import { useTranslate, useGetLocale } from '@refinedev/core';
import { Card, Flex, Tabs, Tag, Typography, theme } from 'antd';
import type { ContentDistributionPeriodDetailOut } from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { orDash } from '@/components/common/show/shared';
import { ShowLink } from '@/components/common/ShowLink';
import { ProductLazyCell } from '@/components/content_catalog/products/ProductLazyCell';
import { PublisherContractTag } from '@/components/content_catalog/distribution_periods/PublisherContractTag';
import { productTypeColors } from '@/pages/content_catalog/products/constants';
import { DISTRIBUTION_PERIOD_SHOW_TABS } from '../constants';
import type { DistributionPeriodShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<DistributionPeriodShowTab, string> = {
  general: 'base.sections.basic_info',
  elibri_events: 'content_catalog.distribution_periods.tabs.elibri_events',
};

interface DistributionPeriodShowHeaderProps {
  record: ContentDistributionPeriodDetailOut;
  activeTab: DistributionPeriodShowTab;
  onTabChange: (key: string) => void;
}

export const DistributionPeriodShowHeader = ({
  record,
  activeTab,
  onTabChange,
}: DistributionPeriodShowHeaderProps) => {
  const t = useTranslate();
  const locale = useGetLocale()();
  const { token } = theme.useToken();
  const f = (key: string) =>
    t(`content_catalog.distribution_periods.fields.${key}`);
  const date = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString(locale) : null;

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginSM} align="baseline" wrap>
        <Title
          level={3}
          style={{
            margin: 0,
            fontSize: token.fontSizeHeading3,
            overflowWrap: 'anywhere',
          }}
        >
          {record.product_title ?? `#${record.id}`}
        </Title>
        {record.product_type && (
          <Tag color={productTypeColors[record.product_type] || 'default'}>
            {t(`content_catalog.products.product_type.${record.product_type}`)}
          </Tag>
        )}
      </Flex>
      <Text
        type="secondary"
        style={{ display: 'block', marginTop: token.marginXXS }}
      >
        {`ID ${record.id}`}
      </Text>

      <Flex
        gap={token.marginXL + token.margin}
        style={{ marginTop: token.marginLG }}
        wrap
      >
        <StatCell label={f('product')}>
          <ProductLazyCell
            productId={record.product_id}
            fallbackTitle={record.product_title}
          />
        </StatCell>
        <StatCell label={f('publisher')}>
          {record.publisher_id != null ? (
            <Flex gap={token.marginXS} align="center" wrap>
              <ShowLink
                resource="content_catalog.publishers"
                id={record.publisher_id}
              >
                {record.publisher_name ?? `#${record.publisher_id}`}
              </ShowLink>
              <PublisherContractTag
                hasActiveContract={record.publisher_has_active_contract}
              />
            </Flex>
          ) : (
            orDash(record.publisher_name)
          )}
        </StatCell>
        <StatCell label={f('period')}>
          {date(record.started_at) ?? '-'} → {date(record.ended_at) ?? '∞'}
        </StatCell>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={DISTRIBUTION_PERIOD_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
